import React, { useState, useEffect, useRef, useCallback } from 'react'
import io from 'socket.io-client';
import uuid from 'uuid-random';
import Card from './Card';
import axios from 'axios';
import Info from './Info';
import { useOutlet } from '../../context/OutletContext';
import DenseCard from './Kitchen/DenseCard';
import { useOutletContext } from 'react-router-dom';
import TimelineView from './Kitchen/TimelineView';
import { HiOutlineMinusCircle } from 'react-icons/hi2';
import { MdOutlineTaskAlt } from "react-icons/md";
import useSoundUnlock from '../../hooks/useSoundUnlock';
import { PiBowlFood } from 'react-icons/pi';

const Order = ({ token, baseUrl }) => {
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({});
    const [show, setShow] = useState(true);
    const [id, setId] = useState("");
    const [hash, setHash] = useState("");
    const [voidQueue, setVoidQueue] = useState([]);
    const [socket, setSocket] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [disconnected, setDisconnected] = useState(false);

    const { playNewOrderSound, speakOrderSummary } = useSoundUnlock();

    const { density = "comfortable" } = useOutletContext() ?? {};

    const gridCols = {
        comfortable: 'grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
        dense: 'grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8',
    };

    const roomIdRef = useRef("");
    const outletRef = useRef("");
    const hashRef = useRef("");
    const listRef = useRef([]);
    const tokenRef = useRef("");

    const url = process.env.REACT_APP_SOCKET_URL;
    const { defaultOutlet } = useOutlet();
    const selectedOutlet = defaultOutlet;

    const speakOrder = (e, orderItems, tableNum) => {
        e.stopPropagation();
        speakOrderSummary(orderItems, { announce: false, tableNum });
    };

    useEffect(() => { roomIdRef.current = id; }, [id]);
    useEffect(() => { outletRef.current = selectedOutlet; }, [selectedOutlet]);
    useEffect(() => { hashRef.current = hash; }, [hash]);
    useEffect(() => { tokenRef.current = token; }, [token]);
    useEffect(() => { listRef.current = list; }, [list]);

    useEffect(() => { setId(uuid()); }, []);

    useEffect(() => {
        if (!url) return;
        const newSocket = io(url, {
            transports: ['websocket'],
            upgrade: false,
            forceNode: true,
            reconnection: false,
        });
        setSocket(newSocket);
        return () => {
            newSocket.removeAllListeners();
            newSocket.disconnect();
        };
    }, [url]);

    const fetchStats = useCallback((outlet, tok) => {
        if (!outlet || !tok) return;
        axios.post(`${baseUrl}/stats`, { outlet, token: tok })
            .then(res => setInfo(res.data))
            .catch(err => console.error("Stats error:", err));
    }, [baseUrl]);

    useEffect(() => {
        if (disconnected) return;
        fetchStats(selectedOutlet, token);
    }, [selectedOutlet, list, fetchStats, token, disconnected]);

    useEffect(() => {
        if (!socket || !selectedOutlet || !token || !id) return;

        const handleJoinError = (err) => {
            const message = err?.message || err?.error || (typeof err === "string" ? err : "Failed to join room");
            setErrorMsg(message);
        };

        const handleConnect = () => {
            setDisconnected(false);
            socket.emit("join", { token, roomCode: selectedOutlet, userName: selectedOutlet });
            socket.emit("get_live", { roomId: id, outlet_name: selectedOutlet, token });
        };

        socket.on("message", handleJoinError);
        if (socket.connected) { handleConnect(); } else { socket.once("connect", handleConnect); }

        return () => {
            socket.off("message", handleJoinError);
            socket.off("connect", handleConnect);
        };
    }, [socket, selectedOutlet, token, id]);

    const updateList = useCallback((data) => {
        const updatedList = [...data];
        listRef.current = updatedList;
        setList(updatedList);
        if (!disconnected && updatedList.length === 0) {
            fetchStats(outletRef.current, tokenRef.current);
        }
    }, [fetchStats, disconnected]);

    const emitGetLive = useCallback(() => {
        if (!socket) return;
        socket.emit("get_live", {
            roomId: roomIdRef.current,
            outlet_name: outletRef.current,
            token: tokenRef.current,
        });
    }, [socket]);

    const emitGetLiveRef = useRef(emitGetLive);
    useEffect(() => { emitGetLiveRef.current = emitGetLive; }, [emitGetLive]);

    useEffect(() => {
        if (!socket) return;

        const onMessage = (msg) => {
            const received_hash = msg.update_endpoint || "";
            setHash(received_hash);
            hashRef.current = received_hash;
            emitGetLiveRef.current();
        };

        const onInitialLoad = (res) => {
            if (!res) { setShow(false); updateList([]); return; }
            const parsed = typeof res === 'string' ? JSON.parse(res) : res;
            const payload = parsed?.data ?? parsed;
            if (Array.isArray(payload)) {
                if (payload.length === 0) { setShow(false); updateList([]); }
                else { setShow(true); setList(payload); listRef.current = payload; }
            } else if (payload && typeof payload === 'object' && payload.table_id) {
                setShow(true);
                const existing = listRef.current;
                const alreadyExists = existing.some(o => o.table_id === payload.table_id);
                if (!alreadyExists) {
                    updateList([payload, ...existing]);
                    playNewOrderSound(payload.OrderItemDetailsList, payload.tableNum);
                }
            } else { setShow(false); updateList([]); }
        };

        const onGetLiveError = (err) => {
            if (disconnected) return;
            setShow(false);
            const msg = err?.message || err?.error || (typeof err === 'string' ? err : "");
            if (msg) setErrorMsg(msg);
            fetchStats(outletRef.current, tokenRef.current);
        };

        const onVoidsUpdate = (data) => {
            setVoidQueue(prev => [...prev, data]);
        };

        const onDisconnect = () => {
            setDisconnected(true);
            setShow(false);
            setHash("");
            setList([]);
            setInfo({});
            setVoidQueue([]);
        };

        const onError = (err) => console.error("Socket error:", err);

        socket.on('message', onMessage);
        socket.on('initial_load', onInitialLoad);
        socket.on('get_live_error', onGetLiveError);
        socket.on('void_update', onVoidsUpdate);
        socket.on('disconnect', onDisconnect);
        socket.on('error', onError);

        return () => {
            socket.off('message', onMessage);
            socket.off('initial_load', onInitialLoad);
            socket.off('get_live_error', onGetLiveError);
            socket.off('void_update', onVoidsUpdate);
            socket.off('disconnect', onDisconnect);
            socket.off('error', onError);
        };
    }, [socket, updateList, fetchStats, playNewOrderSound, disconnected]);

    useEffect(() => {
        if (!socket || !hash) return;

        const onNewOrder = (msg) => {
            if (!msg) return;
            setShow(true);
            updateList([msg, ...listRef.current]);
            playNewOrderSound(msg.OrderItemDetailsList, msg.tableNum);
        };
        const onTableResponse = (res) => updateList(listRef.current.filter(el => el.table_id !== res.table_id));
        const onItemResponse = (res) => { emitGetLiveRef.current(); if (res.item_count === 0) updateList([]); };
        const onQuantityResponse = (res) => {
            const items = [...listRef.current];
            const idx = items.findIndex(i => i.table_id === res.table_id);
            if (idx < 0) return;
            const itemIdx = items[idx].OrderItemDetailsList.findIndex(i => i.item_id === res.item_id);
            if (itemIdx < 0) return;
            items[idx].OrderItemDetailsList[itemIdx].Quantity = res.quantity;
            updateList(items);
        };
        const onRefreshNeeded = () => emitGetLiveRef.current();
        const onError = (err) => {
            if (disconnected) return;
            const msg = err?.message || err?.error || (typeof err === "string" ? err : "An error occurred");
            setErrorMsg(msg);
            fetchStats(outletRef.current, tokenRef.current);
        };

        const errorEvents = [
            `${hash}quantity_error`, `${hash}itemresponse_error`,
            `${hash}itemvoid_error`, `${hash}orderseen_error`, `${hash}tablevoid_error`,
        ];

        socket.on(hash, onNewOrder);
        socket.on(`${hash}table_response`, onTableResponse);
        socket.on(`${hash}item_response`, onItemResponse);
        socket.on(`${hash}itemvoid_response`, onItemResponse);
        socket.on(`${hash}quantity_response`, onQuantityResponse);
        socket.on(`${hash}orderseen_response`, onRefreshNeeded);
        socket.on(`${hash}tablevoid_response`, onRefreshNeeded);
        socket.on(`${hash}deleted_response`, onRefreshNeeded);
        errorEvents.forEach(e => socket.on(e, onError));

        return () => {
            socket.off(hash, onNewOrder);
            socket.off(`${hash}table_response`, onTableResponse);
            socket.off(`${hash}item_response`, onItemResponse);
            socket.off(`${hash}itemvoid_response`, onItemResponse);
            socket.off(`${hash}quantity_response`, onQuantityResponse);
            socket.off(`${hash}orderseen_response`, onRefreshNeeded);
            socket.off(`${hash}tablevoid_response`, onRefreshNeeded);
            socket.off(`${hash}deleted_response`, onRefreshNeeded);
            errorEvents.forEach(e => socket.off(e, onError));
        };
    }, [hash, socket, updateList, playNewOrderSound, fetchStats, disconnected]);

    const handleVoidAcknowledge = () => {
        setVoidQueue(prev => {
            const remaining = prev.slice(1);
            if (remaining.length === 0) {
                setList([]);
                listRef.current = [];
                setInfo({});
                setShow(false);
                emitGetLiveRef.current();
                fetchStats(outletRef.current, tokenRef.current);
            }
            return remaining;
        });
    };

    const handleCompleted = (tableId) => { socket.emit("table_done", { roomId: id, table_id: `${tableId}`, hash: hashRef.current, token }); emitGetLive(); };
    const handleFinished = (itemId, qty) => { socket.emit("item_complete", { roomId: id, item_id: `${itemId}`, hash: hashRef.current, token, quantity: qty }); emitGetLive(); };
    const handleCancel = (itemId) => { socket.emit("item_void", { roomId: id, item_id: `${itemId}`, hash: hashRef.current, token }); emitGetLive(); };
    const handleMinus = (itemId) => { socket.emit("quantity_decrease", { roomId: id, item_id: `${itemId}`, hash: hashRef.current, token }); emitGetLive(); };
    const handleCookProcess = (tableId) => { socket.emit("order_seen", { roomId: id, table_id: `${tableId}`, hash: hashRef.current, token }); emitGetLive(); };

    const currentVoid = voidQueue[0] ?? null;
    const queueRemaining = voidQueue.length;

    return (
        <div className='px-6 py-3 mb-6'>

            {disconnected && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm max-w-4xl mx-auto mt-10">
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-2.5 w-2.5 shrink-0">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                        </span>
                        <span className="font-semibold">Connection lost.</span>
                        <span className="text-red-600">The server is unreachable — live updates are paused.</span>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="shrink-0 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 active:scale-95 transition-all"
                    >
                        Refresh page
                    </button>
                </div>
            )}

            {!disconnected && (
                <>
                    <Info info={info} selectedOutlet={selectedOutlet} />

                    {!show && errorMsg && (
                        <div className="font-semibold text-center text-red-600 text-xl mt-5 w-full">
                            {errorMsg}
                        </div>
                    )}

                    {show && density === "timeline" ? (
                        <TimelineView
                            list={list}
                            handlers={{ handleFinished, handleCompleted, handleMinus, handleCancel, handleCookProcess, speakOrder }}
                        />
                    ) : (
                        <div className={gridCols[density]}>
                            {show && list.map((element, index) =>
                                density === "comfortable" ? (
                                    <Card
                                        key={element.table_id ?? index}
                                        element={element}
                                        handleFinished={handleFinished}
                                        handleCompleted={handleCompleted}
                                        handleMinus={handleMinus}
                                        handleCancel={handleCancel}
                                        handleCookProcess={handleCookProcess}
                                        index={index}
                                        speakOrder={speakOrder}
                                    />
                                ) : (
                                    <DenseCard
                                        key={element.table_id ?? index}
                                        element={element}
                                        index={index}
                                        handleFinished={handleFinished}
                                        handleCompleted={handleCompleted}
                                        handleMinus={handleMinus}
                                        handleCancel={handleCancel}
                                        handleCookProcess={handleCookProcess}
                                        speakOrder={speakOrder}
                                    />
                                )
                            )}
                        </div>
                    )}
                </>
            )}

            {currentVoid && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <div className="w-full max-w-md rounded-xl bg-white border border-[#E0DDD8] overflow-hidden shadow-lg">
                        <div className="p-3 border-b border-[#F0EDE8]">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-[#FCEBEB] flex items-center justify-center shrink-0">
                                        <HiOutlineMinusCircle size={22} className="text-[#A32D2D]" />
                                    </div>
                                    <div className="text-base font-semibold text-[#2C2C2A]">
                                        Items voided
                                    </div>
                                </div>

                                {queueRemaining > 1 && (
                                    <span
                                        className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border"
                                        style={{ background: "#FCEBEB", borderColor: "#F7C1C1", color: "#A32D2D" }}
                                    >
                                        {queueRemaining - 1} more pending
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 p-3 max-h-72 overflow-y-auto tiny-scrollbar">
                            {(currentVoid.items ?? []).map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg border text-sm"
                                    style={{ background: "#FCEBEB", borderColor: "#F7C1C1" }}
                                >
                                    <div className="flex-1 min-w-0 space-y-1.5">
                                        <div className='flex justify-between items-center'>
                                            <div className="flex items-center gap-2.5 flex-wrap text-base font-semibold uppercase tracking-wide">
                                                <span className="px-1.5 py-0.5 rounded" style={{ background: "#F7C1C1", color: "#791F1F" }}>
                                                    Table {item.tableNum}
                                                </span>
                                                <span className="px-1.5 py-0.5 rounded" style={{ background: "#F7C1C1", color: "#791F1F" }}>
                                                    KOT #{item.KOTID}
                                                </span>
                                            </div>

                                            {item.voided_by && (
                                                <div className="text-sm font-semibold" style={{ color: "#A32D2D" }}>
                                                    by {item.voided_by}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between gap-2 bg-white rounded-lg px-2.5 py-1.5 border-[1px] !border-[#F7C1C1]">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <PiBowlFood size={18} className="shrink-0 text-[#A32D2D]" />
                                                <div className="font-semibold text-base break-words" style={{ color: "#501313" }}>
                                                    {item.ItemName}
                                                </div>
                                            </div>

                                            <span
                                                className="text-sm font-semibold px-2 py-0.5 rounded-full shrink-0"
                                                style={{ background: "#F7C1C1", color: "#791F1F" }}
                                            >
                                                ×{item.Quantity}
                                            </span>
                                        </div>

                                        {item.reason && (
                                            <div className="text-xs font-medium" style={{ color: "#A32D2D" }}>
                                                Void Reason: {item.reason}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="px-3 pb-3">
                            <button
                                onClick={handleVoidAcknowledge}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#A32D2D] text-white text-sm font-semibold hover:bg-[#8B2525] active:scale-[0.98] transition-all"
                            >
                                <MdOutlineTaskAlt size={20} />
                                {queueRemaining > 1
                                    ? `Acknowledge · ${queueRemaining - 1} more remaining`
                                    : "Acknowledge"
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Order;